/**
 * useRemoveRsvp Hook
 *
 * Removes RSVP for an event using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const RemoveEventRsvpDocument = `
  mutation RemoveEventRsvp($eventId: ID!) {
    removeEventRsvp(eventId: $eventId)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseRemoveRsvpResult {
  removeRsvp: (eventId: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Remove RSVP for an event.
 *
 * Uses URQL mutation for removing event RSVP.
 */
export function useRemoveRsvp(): UseRemoveRsvpResult {
  const [result, executeMutation] = useMutation<
    { removeEventRsvp: boolean },
    { eventId: string }
  >(RemoveEventRsvpDocument);

  const removeRsvp = useCallback(
    async (eventId: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ eventId });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to remove RSVP"
          );
        }

        errorTracking.addBreadcrumb({
          category: "event",
          message: "RSVP removed",
          data: { eventId },
        });

        return response.data?.removeEventRsvp ?? true;
      } catch (err) {
        const rsvpError =
          err instanceof Error ? err : new Error("Failed to remove RSVP");

        errorTracking.captureError(rsvpError, {
          tags: { feature: "events", action: "removeRsvp" },
          extra: { eventId },
        });

        throw rsvpError;
      }
    },
    [executeMutation]
  );

  return {
    removeRsvp,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
